/**
 * How the conversation reads, as opposed to what it contains.
 *
 * Pure functions over the reducer's state, kept out of the components for the
 * same reason the reducer is: these are the rules worth testing, and none of
 * them needs a DOM.
 */
import type { Segment, Speaker } from '@lt/protocol';
import { isLowConfidence, speakerName, type TranscriptState } from './transcript-state.js';

/**
 * The correction pass's revision band, matching `REVISION_REDIARIZE` in the
 * worker and `REVISION` in packages/fakes/src/script.ts.
 */
export const CORRECTION_REVISION = 3_000;

/**
 * A pause longer than this starts a new run even for the same speaker, so a
 * long monologue split by a silence still shows who picked it back up.
 */
export const RUN_GAP_MICROS = 30_000_000;

/**
 * Whether each segment continues the previous one's speaker.
 *
 * A continued line hides its name: the activity diarizer reads names off the
 * meeting window, so the same name on every one of a speaker's lines is noise
 * rather than information.
 */
export function continuations(segments: readonly Segment[]): boolean[] {
  return segments.map((segment, index) => {
    const previous = segments[index - 1];
    return (
      previous !== undefined &&
      previous.speakerKey === segment.speakerKey &&
      segment.startMicros - previous.endMicros < RUN_GAP_MICROS
    );
  });
}

/**
 * Whether each segment's speaker label can be hidden as a repeat.
 *
 * A continued line hides its name, except where the label turns uncertain:
 * that is the thing a reader most needs to see. Once said, it is not said
 * again for every line of the run — a meeting the diarizer could not read
 * would otherwise carry a question mark on every remote line.
 */
export function hiddenLabels(segments: readonly Segment[]): boolean[] {
  const continued = continuations(segments);
  return segments.map((segment, index) => {
    if (!continued[index]) return false;
    return !isLowConfidence(segment) || isLowConfidence(segments[index - 1]!);
  });
}

/**
 * The speakers worth a chip: those who said something. A diarizer declares
 * every slot it might fill, and a chip for a voice that never spoke is a
 * rename for nobody.
 */
export function speakersHeard(state: TranscriptState): Speaker[] {
  const heard = new Set(state.segments.map((segment) => segment.speakerKey));
  return [...state.speakers.values()]
    .filter((speaker) => heard.has(speaker.key))
    .sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * Each speaker's share of the talking, by time spoken, as a fraction of the
 * whole. A line still arriving counts for what it covers so far.
 */
export function talkShare(segments: readonly Segment[]): Map<string, number> {
  const spoken = new Map<string, number>();
  let total = 0;
  for (const segment of segments) {
    const duration = Math.max(0, segment.endMicros - segment.startMicros);
    spoken.set(segment.speakerKey, (spoken.get(segment.speakerKey) ?? 0) + duration);
    total += duration;
  }
  if (total === 0) return new Map();
  return new Map([...spoken].map(([key, micros]) => [key, micros / total]));
}

/** What the export is called: the day the meeting happened, not the day it was opened. */
export function meetingTitle(startedAt: number | null): string {
  return startedAt === null ? 'Meeting' : `Meeting ${new Date(startedAt).toLocaleDateString()}`;
}

/**
 * Whether `next` is the correction pass changing what `previous` said.
 *
 * Only a change worth seeing counts: the pass re-sends every segment, and one
 * it agreed with should not flash.
 */
export function wasCorrected(previous: Segment | undefined, next: Segment): boolean {
  if (!previous) return false;
  if (previous.revision >= CORRECTION_REVISION || next.revision < CORRECTION_REVISION) return false;
  return previous.text !== next.text || previous.speakerKey !== next.speakerKey;
}

/** `mm:ss`, or `h:mm:ss` past the first hour, from session time. */
export function clock(micros: number): string {
  const total = Math.max(0, Math.floor(micros / 1_000_000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * The transcript as Markdown: one paragraph per speaker run, each opened by
 * the speaker's name and where in the meeting it starts.
 */
export function toMarkdown(state: TranscriptState, title: string): string {
  const lines = [`# ${title}`, ''];
  const continued = continuations(state.segments);
  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length > 0) lines.push(paragraph.join(' '), '');
    paragraph = [];
  };

  state.segments.forEach((segment, index) => {
    const text = segment.text.trim();
    if (!text) return;
    if (!continued[index]) {
      flush();
      paragraph.push(`**${speakerName(state, segment.speakerKey)}** (${clock(segment.startMicros)}):`);
    }
    paragraph.push(text);
  });
  flush();
  return lines.join('\n').trimEnd() + '\n';
}
